/**
 * Long-term memory helpers (v0.5.0): daemon `/memories` wire parsing + the pure
 * decisions behind the Settings 「长期记忆」 section.
 *
 * Memory text is user data (some of it originally model output) and is displayed
 * directly, but every response is untrusted and re-validated here: ids/kinds/
 * statuses, text length and timestamps are checked, and a malformed envelope
 * returns `null` so the UI surfaces a real error instead of fabricated rows.
 *
 * Transport note: `/memories` is a daemon-local HTTP API like `/qq/*`. The phone's
 * live path (E2E relay) only carries `qq.request` today, so live mode reports an
 * explicit unavailable reason; demo mode uses the mock client. See
 * `memoryDaemonAvailability` below.
 */

export type MemoryKind = 'preference' | 'fact' | 'project';
export type MemoryStatus = 'active' | 'pending';

export interface MemoryEntry {
  id: string;
  kind: MemoryKind;
  text: string;
  status: MemoryStatus;
  sensitive: boolean;
  /** Seconds (daemon rows may be epoch ms; normalized at the boundary). */
  created_at: number;
  updated_at: number;
}

export interface MemoryAvailability {
  available: boolean;
  reason?: string;
}

export const MEMORY_KINDS: readonly MemoryKind[] = ['preference', 'fact', 'project'];

export const MEMORY_KIND_LABELS: Record<MemoryKind, string> = {
  preference: '偏好',
  fact: '事实',
  project: '项目',
};

export const MEMORY_STATUS_LABELS: Record<MemoryStatus, string> = {
  active: '已启用',
  pending: '待确认',
};

/** Matches the daemon store bound (500 chars per entry). */
export const MEMORY_TEXT_MAX_CHARS = 500;
export const MEMORY_ID_MAX_CHARS = 128;

const MEMORY_ID_PATTERN = /^[A-Za-z0-9_.:-]{1,128}$/u;
const MEMORY_MS_THRESHOLD = 1e11;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isMemoryKind(value: unknown): value is MemoryKind {
  return value === 'preference' || value === 'fact' || value === 'project';
}

function isMemoryStatus(value: unknown): value is MemoryStatus {
  return value === 'active' || value === 'pending';
}

function normalizedTimestamp(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return 0;
  return Math.floor(value > MEMORY_MS_THRESHOLD ? value / 1000 : value);
}

function cappedText(value: string): string {
  const chars = Array.from(value.trim());
  return chars.length > MEMORY_TEXT_MAX_CHARS
    ? chars.slice(0, MEMORY_TEXT_MAX_CHARS).join('')
    : chars.join('');
}

/** Parse one untrusted memory row; invalid rows are dropped by the caller. */
export function memoryEntryFromUnknown(value: unknown): MemoryEntry | null {
  if (!isRecord(value)) return null;
  const id = value.id;
  const kind = value.kind;
  const status = value.status;
  const rawText = value.text;
  if (typeof id !== 'string' || !MEMORY_ID_PATTERN.test(id)) return null;
  if (!isMemoryKind(kind) || !isMemoryStatus(status)) return null;
  if (typeof value.sensitive !== 'boolean') return null;
  if (typeof rawText !== 'string') return null;
  const text = cappedText(rawText);
  if (text.length === 0) return null;
  const createdAt = normalizedTimestamp(value.created_at);
  const updatedAt = normalizedTimestamp(value.updated_at) || createdAt;
  return {
    id,
    kind,
    text,
    status,
    sensitive: value.sensitive,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

/**
 * Parse a `/memories` response. Accepts both `{ memories: [...] }` (contract) and
 * a bare array (defensive; DELETE responses are "updated list"). Returns `null`
 * only when the envelope itself is invalid; individual bad rows are dropped and
 * duplicate ids collapse (last row wins).
 */
export function memoryEntriesFromUnknown(value: unknown): MemoryEntry[] | null {
  const rawList = Array.isArray(value)
    ? value
    : isRecord(value) && Array.isArray(value.memories)
      ? value.memories
      : null;
  if (rawList === null) return null;
  const entries = new Map<string, MemoryEntry>();
  for (const raw of rawList) {
    const entry = memoryEntryFromUnknown(raw);
    if (entry !== null) entries.set(entry.id, entry);
  }
  return [...entries.values()];
}

export interface PartitionedMemories {
  active: MemoryEntry[];
  pending: MemoryEntry[];
}

/** Split active/pending and sort each group newest-first (input untouched). */
export function partitionMemories(entries: readonly MemoryEntry[]): PartitionedMemories {
  const sorted = [...entries].sort((left, right) => right.updated_at - left.updated_at);
  return {
    active: sorted.filter((entry) => entry.status === 'active'),
    pending: sorted.filter((entry) => entry.status === 'pending'),
  };
}

export function memoryKindLabel(kind: MemoryKind): string {
  return MEMORY_KIND_LABELS[kind];
}

export function memoryStatusLabel(status: MemoryStatus): string {
  return MEMORY_STATUS_LABELS[status];
}

/** Pending memories need an explicit "确认保存" before they are injected. */
export function memoryNeedsApproval(entry: MemoryEntry): boolean {
  return entry.status === 'pending';
}

export interface MemoryCounts {
  total: number;
  active: number;
  pending: number;
}

export function memoryCounts(entries: readonly MemoryEntry[]): MemoryCounts {
  const pending = entries.filter((entry) => entry.status === 'pending').length;
  return { total: entries.length, active: entries.length - pending, pending };
}

/**
 * Demo-only seed used by the mock client so the Settings section is exercisable
 * without a daemon. Clearly labelled as mock in the UI; never persisted.
 */
export function demoMemories(now: number): MemoryEntry[] {
  return [
    {
      id: 'mem_demo_pref_1',
      kind: 'preference',
      text: '喜欢简洁、可执行的回答；不需要寒暄。',
      status: 'active',
      sensitive: false,
      created_at: now - 2 * 86_400,
      updated_at: now - 86_400,
    },
    {
      id: 'mem_demo_fact_1',
      kind: 'fact',
      text: '常用开发环境是 Windows 11 + Node 24 + pnpm。',
      status: 'active',
      sensitive: false,
      created_at: now - 3 * 86_400,
      updated_at: now - 2 * 86_400,
    },
    {
      id: 'mem_demo_project_1',
      kind: 'project',
      text: 'Luminis：私人 AI 助理系统（手机 PWA + PC daemon + Worker）。',
      status: 'active',
      sensitive: false,
      created_at: now - 5 * 86_400,
      updated_at: now - 4 * 86_400,
    },
    {
      id: 'mem_demo_pending_1',
      kind: 'preference',
      text: '希望每周一早上收到本周计划摘要。',
      status: 'pending',
      sensitive: true,
      created_at: now - 3600,
      updated_at: now - 1800,
    },
  ];
}

/**
 * Long-term memory transport availability.
 *
 * `/memories` is a daemon-local HTTP API (device-auth). The live E2E relay only
 * carries `qq.request` today, so live mode reports the exact missing bridge
 * instead of showing/faking memories. Demo mode uses the in-memory mock client.
 *
 * TODO(v0.5.0 memory relay): when the backend adds a `memory.request`/
 * `memory.response` relay op, extend this helper/AppState to use it.
 */
export function memoryDaemonAvailability(input: {
  demoMode: boolean;
  hasClient: boolean;
}): MemoryAvailability {
  if (input.hasClient) return { available: true };
  if (input.demoMode) {
    return { available: false, reason: '演示模式尚未初始化本地设备（请先完成配对）' };
  }
  return {
    available: false,
    reason:
      'live 模式尚未接入 daemon 的 /memories 接口：E2E 中继目前只承载 QQ 管理请求，' +
      '记忆管理需要后端新增中继 op（尚未实现）。此区域不会显示或伪造任何记忆数据。',
  };
}
